import { getMomentTz } from "@dexteel/mesf-core";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Box, Button, Tooltip } from "@mui/material";
import { RECLASSIFIED_COLOR } from "@/shared/colors";
import { toFraction32 } from "@/utils/fraction";
import { CellStyle, ColDef, ICellRendererParams } from "ag-grid-community";
import type { CSSProperties } from "react";
import { useMemo } from "react";
import type { SawQualityCheck } from "../../../repositories/sawWriteRepository";

const moment = getMomentTz();

const POSITIONS: { field: keyof SawQualityCheck; header: string }[] = [
	{ field: "CheckTL", header: "Top Left" },
	{ field: "CheckTR", header: "Top Right" },
	{ field: "CheckMC", header: "Middle Center" },
	{ field: "CheckBL", header: "Bottom Left" },
	{ field: "CheckBR", header: "Bottom Right" },
];

export const isCheckOutOfSpec = (c: SawQualityCheck | undefined): boolean => {
	if (!c || c.MinLength == null || c.MaxLength == null) return false;
	return POSITIONS.some(({ field }) => {
		const v = c[field];
		return (
			typeof v === "number" &&
			(v < (c.MinLength as number) || v > (c.MaxLength as number))
		);
	});
};

// Plain DOM (no MUI) — these cells render per row in both grids.
const positionCellStyle: CSSProperties = { lineHeight: 1.2 };
const positionFractionStyle: CSSProperties = {
	fontSize: "0.7rem",
	color: "#888",
};

const isValueOutOfSpec = (
	c: SawQualityCheck | undefined,
	value: unknown,
): boolean => {
	if (value == null || c?.MinLength == null || c?.MaxLength == null)
		return false;
	const v = Number(value);
	return v < c.MinLength || v > c.MaxLength;
};

export const buildPositionColumns = <
	T extends SawQualityCheck,
>(): ColDef<T>[] =>
	POSITIONS.map(
		({ field, header }): ColDef<T> => ({
			field: field as ColDef<T>["field"],
			headerName: header,
			minWidth: 130,
			flex: 1.3,
			sortable: false,
			filter: false,
			wrapHeaderText: true,
			autoHeaderHeight: true,
			headerStyle: { textAlign: "center" },
			cellRenderer: (params: ICellRendererParams<T>) => {
				if (params.value == null) return null;
				const v = Number(params.value);
				return (
					<span style={positionCellStyle}>
						{String(v)}
						<span style={positionFractionStyle}> ({toFraction32(v)})</span>
					</span>
				);
			},
			tooltipValueGetter: (params) =>
				isValueOutOfSpec(params.data, params.value)
					? `Out of spec — allowed ${params.data?.MinLength} / ${params.data?.MaxLength}`
					: "",
			cellStyle: (params) => {
				// The custom renderer would otherwise sit at the top of the row.
				const style: CellStyle = {
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				};
				if (isValueOutOfSpec(params.data, params.value)) {
					style.color = "#d32f2f";
					style.fontWeight = 700;
				}
				return style;
			},
		}),
	);

export const buildBundleColumns = <
	T extends SawQualityCheck,
>(): ColDef<T>[] => [
	{
		field: "BundleId" as ColDef<T>["field"],
		headerName: "Bundle Pieces",
		minWidth: 100,
		flex: 1.1,
		wrapHeaderText: true,
		autoHeaderHeight: true,
		valueGetter: (params) => {
			const d = params.data;
			if (!d || d.BundleId == null) return "";
			if (d.IsCutback) return "Cutback";
			return d.BundleBarCount ?? "";
		},
		cellStyle: (params): CellStyle =>
			params.data?.IsCutback
				? { color: "#757575", fontWeight: 600, textAlign: "center" }
				: { textAlign: "center" },
	},
	{
		field: "BundleStartTime" as ColDef<T>["field"],
		headerName: "Start",
		minWidth: 115,
		flex: 1.2,
		valueFormatter: (params) =>
			params.value ? moment(params.value).format("HH:mm - DD/MM") : "",
	},
	{
		field: "BundleEndTime" as ColDef<T>["field"],
		headerName: "End",
		minWidth: 115,
		flex: 1.2,
		valueFormatter: (params) =>
			params.value ? moment(params.value).format("HH:mm - DD/MM") : "",
	},
];

type CheckActionHandlers<T> = {
	onReclassify: (check: T) => void;
	onEdit: (check: T) => void;
	onDelete: (check: T) => void;
};

const actionBtnSx = { minWidth: 28, padding: "4px" } as const;

export const buildCheckActionsColumn = <T extends SawQualityCheck>({
	onReclassify,
	onEdit,
	onDelete,
}: CheckActionHandlers<T>): ColDef<T> => ({
	colId: "actions",
	headerName: "",
	minWidth: 130,
	maxWidth: 130,
	sortable: false,
	filter: false,
	cellStyle: { padding: 0 },
	cellRenderer: (params: ICellRendererParams<T>) => {
		const check = params.data;
		if (!check) return null;
		// Reclassify/edit are bundle-scoped; an orphan check can only be deleted.
		const hasBundle = check.BundleId != null;
		const outOfSpec = isCheckOutOfSpec(check);
		return (
			<Box
				sx={{
					height: "100%",
					display: "flex",
					justifyContent: "space-around",
					alignItems: "center",
				}}
			>
				<Box sx={{ display: "flex", gap: 0 }}>
					<Tooltip
						title={
							!hasBundle
								? "No bundle linked — nothing to reclassify"
								: outOfSpec
									? "Out of spec — reclassify this bundle"
									: "Reclassify this bundle"
						}
					>
						{/* span keeps the tooltip alive while the button is disabled */}
						<span>
							<Button
								size="small"
								sx={actionBtnSx}
								disabled={!hasBundle}
								onClick={() => onReclassify(check)}
							>
								<SwapHorizIcon
									sx={{ color: hasBundle ? RECLASSIFIED_COLOR : undefined }}
								/>
							</Button>
						</span>
					</Tooltip>
					<Tooltip
						title={
							hasBundle
								? "Edit this check (values or bundle)"
								: "No bundle linked — nothing to edit"
						}
					>
						<span>
							<Button
								size="small"
								sx={actionBtnSx}
								disabled={!hasBundle}
								onClick={() => onEdit(check)}
							>
								<EditIcon color={hasBundle ? "primary" : "disabled"} />
							</Button>
						</span>
					</Tooltip>
					<Tooltip title="Delete this check">
						<span>
							<Button
								size="small"
								sx={actionBtnSx}
								onClick={() => onDelete(check)}
							>
								<DeleteIcon color="secondary" />
							</Button>
						</span>
					</Tooltip>
				</Box>
			</Box>
		);
	},
});

type Props = {
	onReclassify: (check: SawQualityCheck) => void;
	onEdit: (check: SawQualityCheck) => void;
	onDelete: (check: SawQualityCheck) => void;
};

export const useTableData = ({ onReclassify, onEdit, onDelete }: Props) => {
	const columnDefs = useMemo(
		(): ColDef<SawQualityCheck>[] => [
			{
				field: "CheckTime",
				headerName: "Time",
				minWidth: 62,
				maxWidth: 62,
				flex: 1,
				valueFormatter: (params) =>
					params.value ? moment(params.value).format("HH:mm") : "",
			},
			...buildBundleColumns(),
			...buildPositionColumns(),
			{
				field: "CreatedBy",
				headerName: "By",
				minWidth: 80,
				flex: 1,
				valueFormatter: (params) => params.value ?? "",
			},
			buildCheckActionsColumn({ onReclassify, onEdit, onDelete }),
		],
		[onReclassify, onEdit, onDelete],
	);

	return { columnDefs };
};
